import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

const SHAPE_CONFIGS = {
  round: {
    width: 80,
    height: 80,
    minWidth: 50,
    minHeight: 50,
    borderRadius: '50%',
    label: 'Round',
    seatPattern: 'circle',
    seatsPerUnit: 0.1,
    defaultSeats: 12,
  },
  rectangle: {
    width: 120,
    height: 60,
    minWidth: 60,
    minHeight: 40,
    borderRadius: '8px',
    label: 'Long Table',
    seatPattern: 'rectangle',
    seatsPerUnit: 0.06,
    defaultSeats: 12,
  },
  square: {
    width: 80,
    height: 80,
    minWidth: 50,
    minHeight: 50,
    borderRadius: '8px',
    label: 'Square',
    seatPattern: 'square',
    seatsPerUnit: 0.08,
    defaultSeats: 12,
  },
  oval: {
    width: 120,
    height: 80,
    minWidth: 80,
    minHeight: 50,
    borderRadius: '50%',
    label: 'Oval',
    seatPattern: 'oval', // seats arranged in oval
    seatsPerUnit: 0.08,
  },
  lshape: {
    width: 120,
    height: 120,
    minWidth: 80,
    minHeight: 80,
    borderRadius: '8px',
    label: 'L-Shape',
    seatPattern: 'lshape',
    seatsPerUnit: 0.05,
    defaultSeats: 14,
  },
  ushape: {
    width: 140,
    height: 100,
    minWidth: 100,
    minHeight: 70,
    borderRadius: '8px',
    label: 'U-Shape',
    seatPattern: 'ushape',
    seatsPerUnit: 0.05,
    defaultSeats: 18,
  },
  sshape: {
    width: 240,
    height: 120,
    minWidth: 160,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 2',
    seatPattern: 'sshape',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  crescent: {
    width: 140,
    height: 80,
    minWidth: 90,
    minHeight: 50,
    borderRadius: '40%',
    label: 'Crescent',
    seatPattern: 'crescent',
    seatsPerUnit: 0.06,
  },
  serpentine3: {
    width: 360,
    height: 120,
    minWidth: 240,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 3',
    seatPattern: 'serpentine3',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  serpentine4: {
    width: 480,
    height: 120,
    minWidth: 320,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 4',
    seatPattern: 'serpentine4',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  serpentine5: {
    width: 600,
    height: 120,
    minWidth: 400,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 5',
    seatPattern: 'serpentine5',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  serpentine6: {
    width: 720,
    height: 120,
    minWidth: 480,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 6',
    seatPattern: 'serpentine6',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  serpentine7: {
    width: 840,
    height: 120,
    minWidth: 560,
    minHeight: 80,
    borderRadius: '0',
    label: 'Serpentine 7',
    seatPattern: 'serpentine7',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
  halfround: {
    width: 200,
    height: 100,
    minWidth: 140,
    minHeight: 70,
    borderRadius: '0',
    label: 'Half Round',
    seatPattern: 'halfround',
    seatsPerUnit: 0.03,
    isCustomSvg: true,
  },
};

// Calculate auto seats based on table size
function calculateAutoSeats(shape, width, height) {
  const config = SHAPE_CONFIGS[shape] || SHAPE_CONFIGS.round;
  const perimeter = shape === 'round' || shape === 'circle' 
    ? Math.PI * Math.min(width, height)
    : shape === 'oval'
      ? Math.PI * (width + height) / 2
      : 2 * (width + height);
  
  const seats = Math.max(2, Math.round(perimeter * (config.seatsPerUnit || 0.08)));
  return Math.min(seats, 20); // Cap at 20 seats
}

// Calculate seat positions based on shape type
function getSeatPositions(shape, seats, width, height) {
  const positions = [];
  const seatSize = 8; // size of seat indicator
  const padding = 6; // distance from table edge
  
  switch (shape) {
    case 'round':
    case 'circle': {
      // For round tables, seats form a perfect circle
      // Center is at width/2, height/2
      const centerX = width / 2;
      const centerY = height / 2;
      
      // Use the larger dimension to determine seat radius (ensures circle, not oval)
      const tableRadius = Math.max(width, height) / 2;
      const seatCountFactor = Math.max(1, seats / 8); // baseline is 8 seats
      const dynamicPadding = padding + (seatCountFactor > 1 ? (seatCountFactor - 1) * 4 : 0);
      const seatRadius = tableRadius + dynamicPadding;
      
      for (let i = 0; i < seats; i++) {
        // Start from top (12 o'clock position) and go clockwise
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        // Offset adjusted for better visual centering (seats pushed up-left)
        positions.push({
          x: centerX + Math.cos(angle) * seatRadius - seatSize / 2 - 3,
          y: centerY + Math.sin(angle) * seatRadius - seatSize / 2 - 3,
        });
      }
      break;
    }
    case 'oval': {
      // Dynamically adjust seat distance based on seat count
      const seatCountFactor = Math.max(1, seats / 10); // baseline is 10 seats for oval
      const dynamicPadding = padding + (seatCountFactor > 1 ? (seatCountFactor - 1) * 4 : 0);
      const rx = (width / 2) + dynamicPadding;
      const ry = (height / 2) + dynamicPadding;
      
      for (let i = 0; i < seats; i++) {
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        // Offset adjusted for better visual centering (seats pushed up-left)
        positions.push({
          x: width / 2 + Math.cos(angle) * rx - seatSize / 2 - 3,
          y: height / 2 + Math.sin(angle) * ry - seatSize / 2 - 3,
        });
      }
      break;
    }
    case 'rectangle': {
      // Seats on long sides only
      const seatsPerSide = Math.ceil(seats / 2);
      const spacing = (width - 20) / (seatsPerSide + 1);
      for (let i = 0; i < Math.min(seatsPerSide, seats); i++) {
        // Top seats pushed up for better vertical centering
        positions.push({ x: 10 + spacing * (i + 1) - seatSize / 2, y: -padding - seatSize - 1 }); // top
      }
      for (let i = 0; i < Math.min(seatsPerSide, seats - seatsPerSide); i++) {
        positions.push({ x: 10 + spacing * (i + 1) - seatSize / 2, y: height + padding - seatSize / 2 }); // bottom
      }
      break;
    }
    case 'square': {
      // Seats on all 4 sides
      const seatsPerSide = Math.ceil(seats / 4);
      const sideSeats = [
        Math.min(seatsPerSide, seats),
        Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide)),
        Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide * 2)),
        Math.min(seatsPerSide, Math.max(0, seats - seatsPerSide * 3)),
      ];
      // Top - pushed up for better vertical centering
      for (let i = 0; i < sideSeats[0]; i++) {
        const spacing = width / (sideSeats[0] + 1);
        positions.push({ x: spacing * (i + 1) - seatSize / 2, y: -padding - seatSize - 1 });
      }
      // Right
      for (let i = 0; i < sideSeats[1]; i++) {
        const spacing = height / (sideSeats[1] + 1);
        positions.push({ x: width + padding - seatSize / 2, y: spacing * (i + 1) - seatSize / 2 });
      }
      // Bottom
      for (let i = 0; i < sideSeats[2]; i++) {
        const spacing = width / (sideSeats[2] + 1);
        positions.push({ x: spacing * (i + 1) - seatSize / 2, y: height + padding - seatSize / 2 });
      }
      // Left - pushed left for better horizontal centering
      for (let i = 0; i < sideSeats[3]; i++) {
        const spacing = height / (sideSeats[3] + 1);
        positions.push({ x: -padding - seatSize - 1, y: spacing * (i + 1) - seatSize / 2 });
      }
      break;
    }
    case 'lshape': {
      // L-shape: seats on outer edges + inner edges
      // Layout: outer seats (left + bottom) + inner seats (right inner + top inner)
      // Default: 4 left outer, 4 bottom outer, 3 right inner, 3 top inner = 14 total
      const totalSeats = seats;
      
      // Calculate seat distribution - outer seats have priority
      // Minimum: 4 left outer, 4 bottom outer (8 outer minimum)
      const minOuterSeats = 8;
      const outerSeats = Math.max(minOuterSeats, Math.floor(totalSeats * 0.57)); // ~57% outer
      const innerSeats = totalSeats - outerSeats;
      
      // Split outer seats between left and bottom
      const leftOuter = Math.ceil(outerSeats / 2);
      const bottomOuter = outerSeats - leftOuter;
      
      // Split inner seats between right inner and top inner
      const rightInner = Math.ceil(innerSeats / 2);
      const topInner = innerSeats - rightInner;
      
      // Left outer (full height of vertical part)
      for (let i = 0; i < leftOuter; i++) {
        const spacing = height / (leftOuter + 1);
        positions.push({ x: -padding - seatSize - 1, y: spacing * (i + 1) - seatSize / 2 });
      }
      
      // Bottom outer (full width of horizontal part)
      for (let i = 0; i < bottomOuter; i++) {
        const spacing = width / (bottomOuter + 1);
        positions.push({ x: spacing * (i + 1) - seatSize / 2, y: height + padding - seatSize / 2 });
      }
      
      // Right inner (inside the L, on the right side of vertical part)
      // More space between seats - span almost full height of vertical part
      if (rightInner > 0) {
        const verticalInnerStartY = height * 0.08;
        const verticalInnerHeight = height * 0.65;
        for (let i = 0; i < rightInner; i++) {
          const spacing = verticalInnerHeight / (rightInner + 1);
          positions.push({ x: width * 0.25 + padding + 2, y: verticalInnerStartY + spacing * (i + 1) - seatSize / 2 });
        }
      }
      
      // Top inner (inside the L, on top of horizontal part)
      // Move LEFT: start from width * 0.30 instead of 0.45
      if (topInner > 0) {
        const horizontalInnerStartX = width * 0.30;
        const horizontalInnerWidth = width * 0.55;
        for (let i = 0; i < topInner; i++) {
          const spacing = horizontalInnerWidth / (topInner + 1);
          positions.push({ x: horizontalInnerStartX + spacing * (i + 1) - seatSize / 2, y: height * 0.75 - padding - seatSize - 1 });
        }
      }
      break;
    }
    case 'ushape': {
      // U-shape: seats on outer edges + inner edges
      // Layout: outer seats (left + bottom + right) + inner seats (left inner + top inner + right inner)
      // Default: 4 left outer, 4 bottom outer, 4 right outer, 2 left inner, 2 top inner, 2 right inner = 18 total
      const totalSeats = seats;
      
      // Calculate seat distribution - outer seats have priority
      // Minimum: 4 left outer, 4 bottom outer, 4 right outer (12 outer minimum)
      const minOuterSeats = 12;
      const outerSeats = Math.max(minOuterSeats, Math.floor(totalSeats * 0.67)); // ~67% outer
      const innerSeats = totalSeats - outerSeats;
      
      // Split outer seats equally between left, bottom, and right
      const outerPerSide = Math.floor(outerSeats / 3);
      const bottomOuter = outerSeats - (outerPerSide * 2);
      const leftOuter = outerPerSide;
      const rightOuter = outerPerSide;
      
      // Split inner seats equally between left inner, top inner, and right inner
      const innerPerSide = Math.floor(innerSeats / 3);
      const topInner = innerSeats - (innerPerSide * 2);
      const leftInner = innerPerSide;
      const rightInner = innerPerSide;
      
      // Left outer
      for (let i = 0; i < leftOuter; i++) {
        const spacing = (height - 20) / (leftOuter + 1);
        positions.push({ x: -padding - seatSize - 1, y: 10 + spacing * (i + 1) - seatSize / 2 });
      }
      
      // Bottom outer
      for (let i = 0; i < bottomOuter; i++) {
        const spacing = (width - 40) / (bottomOuter + 1);
        positions.push({ x: 20 + spacing * (i + 1) - seatSize / 2, y: height + padding - seatSize / 2 });
      }
      
      // Right outer
      for (let i = 0; i < rightOuter; i++) {
        const spacing = (height - 20) / (rightOuter + 1);
        positions.push({ x: width + padding - seatSize / 2, y: 10 + spacing * (i + 1) - seatSize / 2 });
      }
      
      // Inner seats (inside the U opening)
      if (innerSeats > 0) {
        const innerWidth = width * 0.5;  // middle 50% of width
        const innerStartX = width * 0.25;
        
        // Inner vertical positions: Move UP - start earlier
        const innerVerticalStartY = height * 0.05;
        const innerVerticalHeight = height * 0.45;
        
        // Top inner Y position - Move UP (less close to bottom)
        const innerTopY = height * 0.50;
        
        // Left inner - moved up
        for (let i = 0; i < leftInner; i++) {
          const spacing = innerVerticalHeight / (leftInner + 1);
          positions.push({ x: innerStartX + padding, y: innerVerticalStartY + spacing * (i + 1) - seatSize / 2 });
        }
        
        // Top inner (moved up - less close to table bottom)
        for (let i = 0; i < topInner; i++) {
          const spacing = innerWidth / (topInner + 1);
          positions.push({ x: innerStartX + spacing * (i + 1) - seatSize / 2, y: innerTopY - seatSize / 2 });
        }
        
        // Right inner - moved up
        for (let i = 0; i < rightInner; i++) {
          const spacing = innerVerticalHeight / (rightInner + 1);
          positions.push({ x: width * 0.75 - padding - seatSize, y: innerVerticalStartY + spacing * (i + 1) - seatSize / 2 });
        }
      }
      break;
    }
    case 'sshape': {
      // Serpentine 2-arc table with 12 seats
      // Pattern: Arc 1 UP, Arc 2 DOWN
      // ViewBox based on first 2 arcs of the serpentine pattern
      
      const viewBoxWidth = 510;
      const viewBoxHeight = 290;
      const viewBoxX = 380;
      const viewBoxY = 290;
      
      // Seat positions for 2 arcs (6 seats each)
      const svgSeatPositions = [
        // === Arc 1 - curves UP - 6 seats ===
        { x: 408.4, y: 351.4 },   // Outer left
        { x: 480.7, y: 323.5 },   // Outer top-left
        { x: 561.4, y: 323.9 },   // Outer top-right
        { x: 634.8, y: 351.8 },   // Outer right
        { x: 492.8, y: 446.6 },   // Inner left
        { x: 552.3, y: 447.6 },   // Inner right
        
        // === Arc 2 - curves DOWN - 6 seats ===
        { x: 627.1, y: 499.6 },   // Bottom outer left
        { x: 699.4, y: 527.5 },   // Bottom far left
        { x: 780.1, y: 527.1 },   // Bottom far right
        { x: 853.5, y: 499.2 },   // Bottom outer right
        { x: 711.5, y: 404.4 },   // Top inner left
        { x: 771.0, y: 403.4 },   // Top inner right
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'crescent': {
      // Crescent: seats on inner curve
      const radius = (width / 2) - 10;
      for (let i = 0; i < seats; i++) {
        const angle = (Math.PI * 0.7 * i) / (seats - 1 || 1) + Math.PI * 0.15;
        positions.push({
          x: width / 2 + Math.cos(angle) * radius - seatSize / 2,
          y: height / 2 + Math.sin(angle) * (radius * 0.6) - seatSize / 2,
        });
      }
      break;
    }
    case 'serpentine3': {
      // Serpentine 3-arc table with 18 seats
      // The SVG viewBox is "70 260 710 290" which means:
      // - Content starts at x=70, y=260
      // - Content spans 710 units wide, 290 units tall
      // - So content ends at x=780, y=550
      
      // The seat positions need to be mapped from SVG viewBox coordinates to table dimensions
      // We need to calculate where each seat falls as a percentage of the viewBox, 
      // then apply that percentage to the actual table width/height
      
      const viewBoxWidth = 710;
      const viewBoxHeight = 290;
      const viewBoxX = 70;
      const viewBoxY = 260;
      
      // Seat positions in original SVG coordinates (cx, cy from the circles)
      // These are the absolute positions in the SVG's coordinate system
      const svgSeatPositions = [
        // === Arc 1 (Left) - 6 seats ===
        { x: 92.8, y: 351.6 },    // Outer left
        { x: 165.1, y: 323.7 },   // Outer top-left
        { x: 245.8, y: 324.1 },   // Outer top-right
        { x: 319.2, y: 352.0 },   // Outer right
        { x: 177.2, y: 446.8 },   // Inner left
        { x: 236.7, y: 447.8 },   // Inner right
        
        // === Arc 2 (Middle inverted) - 6 seats ===
        { x: 311.5, y: 499.8 },   // Bottom outer left
        { x: 383.8, y: 527.7 },   // Bottom far left
        { x: 464.5, y: 527.3 },   // Bottom far right
        { x: 537.9, y: 499.4 },   // Bottom outer right
        { x: 395.9, y: 404.6 },   // Top inner left
        { x: 455.4, y: 403.6 },   // Top inner right
        
        // === Arc 3 (Right) - 6 seats ===
        { x: 530.7, y: 351.5 },   // Outer left
        { x: 603.0, y: 323.6 },   // Outer top-left
        { x: 683.7, y: 324.0 },   // Outer top-right
        { x: 757.1, y: 351.8 },   // Outer right
        { x: 615.1, y: 446.7 },   // Inner left
        { x: 674.6, y: 447.7 },   // Inner right
      ];
      
      // Convert from SVG coordinates to percentage positions within viewBox
      // Then apply to actual table dimensions
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        // Calculate percentage position within the viewBox
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        // Apply percentage to actual table dimensions
        // The seat indicator should be positioned at the center of where the seat appears
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'serpentine4': {
      // Serpentine 4-arc table with 24 seats from user's SVG
      // ViewBox: 380 290 950 290 (cropped from original 0 0 1700.8 850.4)
      
      const viewBoxWidth = 950;
      const viewBoxHeight = 290;
      const viewBoxX = 380;
      const viewBoxY = 290;
      
      // Seat positions from user's SVG circles (cx, cy)
      const svgSeatPositions = [
        // === Arc 1 (Left) - curves UP - 6 seats ===
        { x: 408.4, y: 351.4 },   // Outer left
        { x: 480.7, y: 323.5 },   // Outer top-left
        { x: 561.4, y: 323.9 },   // Outer top-right
        { x: 634.8, y: 351.8 },   // Outer right
        { x: 492.8, y: 446.6 },   // Inner left
        { x: 552.3, y: 447.6 },   // Inner right
        
        // === Arc 2 - curves DOWN - 6 seats ===
        { x: 627.1, y: 499.6 },   // Bottom outer left
        { x: 699.4, y: 527.5 },   // Bottom far left
        { x: 780.1, y: 527.1 },   // Bottom far right
        { x: 853.5, y: 499.2 },   // Bottom outer right
        { x: 711.5, y: 404.4 },   // Top inner left
        { x: 771.0, y: 403.4 },   // Top inner right
        
        // === Arc 3 - curves UP - 6 seats ===
        { x: 846.3, y: 351.3 },   // Outer left
        { x: 918.6, y: 323.4 },   // Outer top-left
        { x: 999.3, y: 323.8 },   // Outer top-right
        { x: 1072.7, y: 351.6 },  // Outer right
        { x: 930.7, y: 446.5 },   // Inner left
        { x: 990.2, y: 447.5 },   // Inner right
        
        // === Arc 4 (Right) - curves DOWN - 6 seats ===
        { x: 1065.5, y: 499.9 },  // Bottom outer left
        { x: 1137.8, y: 527.9 },  // Bottom far left
        { x: 1218.5, y: 527.5 },  // Bottom far right
        { x: 1291.9, y: 499.6 },  // Bottom outer right
        { x: 1149.9, y: 404.8 },  // Top inner left
        { x: 1209.5, y: 403.8 },  // Top inner right
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'serpentine5': {
      // Serpentine 5-arc table with 30 seats
      // Pattern: Arc UP → Arc DOWN → Arc UP → Arc DOWN → Arc UP
      // ViewBox extended for 5 arcs
      
      const viewBoxWidth = 1170;  // Extended from 950 to accommodate Arc 5
      const viewBoxHeight = 290;
      const viewBoxX = 380;
      const viewBoxY = 290;
      
      // Arc spacing is approximately 219 units between arc starts
      const arcSpacing = 219;
      
      // Seat positions - Arc 5 is Arc 3 pattern shifted by 2*arcSpacing (438)
      const svgSeatPositions = [
        // === Arc 1 (Left) - curves UP - 6 seats ===
        { x: 408.4, y: 351.4 },   // Outer left
        { x: 480.7, y: 323.5 },   // Outer top-left
        { x: 561.4, y: 323.9 },   // Outer top-right
        { x: 634.8, y: 351.8 },   // Outer right
        { x: 492.8, y: 446.6 },   // Inner left
        { x: 552.3, y: 447.6 },   // Inner right
        
        // === Arc 2 - curves DOWN - 6 seats ===
        { x: 627.1, y: 499.6 },   // Bottom outer left
        { x: 699.4, y: 527.5 },   // Bottom far left
        { x: 780.1, y: 527.1 },   // Bottom far right
        { x: 853.5, y: 499.2 },   // Bottom outer right
        { x: 711.5, y: 404.4 },   // Top inner left
        { x: 771.0, y: 403.4 },   // Top inner right
        
        // === Arc 3 - curves UP - 6 seats ===
        { x: 846.3, y: 351.3 },   // Outer left
        { x: 918.6, y: 323.4 },   // Outer top-left
        { x: 999.3, y: 323.8 },   // Outer top-right
        { x: 1072.7, y: 351.6 },  // Outer right
        { x: 930.7, y: 446.5 },   // Inner left
        { x: 990.2, y: 447.5 },   // Inner right
        
        // === Arc 4 - curves DOWN - 6 seats ===
        { x: 1065.5, y: 499.9 },  // Bottom outer left
        { x: 1137.8, y: 527.9 },  // Bottom far left
        { x: 1218.5, y: 527.5 },  // Bottom far right
        { x: 1291.9, y: 499.6 },  // Bottom outer right
        { x: 1149.9, y: 404.8 },  // Top inner left
        { x: 1209.5, y: 403.8 },  // Top inner right
        
        // === Arc 5 (Right) - curves UP - 6 seats (Arc 3 + 438) ===
        { x: 846.3 + 438, y: 351.3 },   // Outer left = 1284.3
        { x: 918.6 + 438, y: 323.4 },   // Outer top-left = 1356.6
        { x: 999.3 + 438, y: 323.8 },   // Outer top-right = 1437.3
        { x: 1072.7 + 438, y: 351.6 },  // Outer right = 1510.7
        { x: 930.7 + 438, y: 446.5 },   // Inner left = 1368.7
        { x: 990.2 + 438, y: 447.5 },   // Inner right = 1428.2
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'serpentine6': {
      // Serpentine 6-arc table with 36 seats
      // Pattern: Arc UP → DOWN → UP → DOWN → UP → DOWN
      // Arc spacing is ~219 units
      
      const viewBoxWidth = 1390;  // Extended for 6 arcs
      const viewBoxHeight = 290;
      const viewBoxX = 380;
      const viewBoxY = 290;
      
      const svgSeatPositions = [
        // === Arc 1 - curves UP - 6 seats ===
        { x: 408.4, y: 351.4 },
        { x: 480.7, y: 323.5 },
        { x: 561.4, y: 323.9 },
        { x: 634.8, y: 351.8 },
        { x: 492.8, y: 446.6 },
        { x: 552.3, y: 447.6 },
        
        // === Arc 2 - curves DOWN - 6 seats ===
        { x: 627.1, y: 499.6 },
        { x: 699.4, y: 527.5 },
        { x: 780.1, y: 527.1 },
        { x: 853.5, y: 499.2 },
        { x: 711.5, y: 404.4 },
        { x: 771.0, y: 403.4 },
        
        // === Arc 3 - curves UP - 6 seats ===
        { x: 846.3, y: 351.3 },
        { x: 918.6, y: 323.4 },
        { x: 999.3, y: 323.8 },
        { x: 1072.7, y: 351.6 },
        { x: 930.7, y: 446.5 },
        { x: 990.2, y: 447.5 },
        
        // === Arc 4 - curves DOWN - 6 seats ===
        { x: 1065.5, y: 499.9 },
        { x: 1137.8, y: 527.9 },
        { x: 1218.5, y: 527.5 },
        { x: 1291.9, y: 499.6 },
        { x: 1149.9, y: 404.8 },
        { x: 1209.5, y: 403.8 },
        
        // === Arc 5 - curves UP - 6 seats (Arc 3 + 438) ===
        { x: 1284.3, y: 351.3 },
        { x: 1356.6, y: 323.4 },
        { x: 1437.3, y: 323.8 },
        { x: 1510.7, y: 351.6 },
        { x: 1368.7, y: 446.5 },
        { x: 1428.2, y: 447.5 },
        
        // === Arc 6 - curves DOWN - 6 seats (Arc 4 + 438) ===
        { x: 1503.5, y: 499.9 },
        { x: 1575.8, y: 527.9 },
        { x: 1656.5, y: 527.5 },
        { x: 1729.9, y: 499.6 },
        { x: 1587.9, y: 404.8 },
        { x: 1647.5, y: 403.8 },
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'serpentine7': {
      // Serpentine 7-arc table with 42 seats
      // Pattern: Arc UP → DOWN → UP → DOWN → UP → DOWN → UP
      
      const viewBoxWidth = 1610;  // Extended for 7 arcs
      const viewBoxHeight = 290;
      const viewBoxX = 380;
      const viewBoxY = 290;
      
      const svgSeatPositions = [
        // === Arc 1 - curves UP - 6 seats ===
        { x: 408.4, y: 351.4 },
        { x: 480.7, y: 323.5 },
        { x: 561.4, y: 323.9 },
        { x: 634.8, y: 351.8 },
        { x: 492.8, y: 446.6 },
        { x: 552.3, y: 447.6 },
        
        // === Arc 2 - curves DOWN - 6 seats ===
        { x: 627.1, y: 499.6 },
        { x: 699.4, y: 527.5 },
        { x: 780.1, y: 527.1 },
        { x: 853.5, y: 499.2 },
        { x: 711.5, y: 404.4 },
        { x: 771.0, y: 403.4 },
        
        // === Arc 3 - curves UP - 6 seats ===
        { x: 846.3, y: 351.3 },
        { x: 918.6, y: 323.4 },
        { x: 999.3, y: 323.8 },
        { x: 1072.7, y: 351.6 },
        { x: 930.7, y: 446.5 },
        { x: 990.2, y: 447.5 },
        
        // === Arc 4 - curves DOWN - 6 seats ===
        { x: 1065.5, y: 499.9 },
        { x: 1137.8, y: 527.9 },
        { x: 1218.5, y: 527.5 },
        { x: 1291.9, y: 499.6 },
        { x: 1149.9, y: 404.8 },
        { x: 1209.5, y: 403.8 },
        
        // === Arc 5 - curves UP - 6 seats (Arc 3 + 438) ===
        { x: 1284.3, y: 351.3 },
        { x: 1356.6, y: 323.4 },
        { x: 1437.3, y: 323.8 },
        { x: 1510.7, y: 351.6 },
        { x: 1368.7, y: 446.5 },
        { x: 1428.2, y: 447.5 },
        
        // === Arc 6 - curves DOWN - 6 seats (Arc 4 + 438) ===
        { x: 1503.5, y: 499.9 },
        { x: 1575.8, y: 527.9 },
        { x: 1656.5, y: 527.5 },
        { x: 1729.9, y: 499.6 },
        { x: 1587.9, y: 404.8 },
        { x: 1647.5, y: 403.8 },
        
        // === Arc 7 - curves UP - 6 seats (Arc 5 + 438) ===
        { x: 1722.3, y: 351.3 },
        { x: 1794.6, y: 323.4 },
        { x: 1875.3, y: 323.8 },
        { x: 1948.7, y: 351.6 },
        { x: 1806.7, y: 446.5 },
        { x: 1866.2, y: 447.5 },
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    case 'halfround': {
      // Half Round table with 12 seats (2 arcs, exact SVG proportions)
      // ViewBox: 200 310 450 220 (exact content bounds from SVG)
      const viewBoxWidth = 450;
      const viewBoxHeight = 220;
      const viewBoxX = 200;
      const viewBoxY = 310;
      
      // Seat positions from user's SVG circles (cx, cy)
      const svgSeatPositions = [
        // === Left arc - 6 seats (outer curve) ===
        { x: 215.3, y: 486.3 },   // Bottom outer-left
        { x: 248.0, y: 414.9 },   // Middle outer-left
        { x: 305.2, y: 358.0 },   // Upper outer-left
        { x: 376.3, y: 327.2 },   // Top center-left
        // Inner seats
        { x: 341.3, y: 496.7 },   // Bottom inner-left
        { x: 383.0, y: 454.2 },   // Center inner-left
        
        // === Right arc - 6 seats (outer curve) ===
        { x: 475.9, y: 328.0 },   // Top center-right
        { x: 546.6, y: 359.8 },   // Upper outer-right
        { x: 603.0, y: 417.5 },   // Middle outer-right
        { x: 634.7, y: 489.4 },   // Bottom outer-right
        // Inner seats
        { x: 467.5, y: 454.9 },   // Center inner-right
        { x: 508.6, y: 498.0 },   // Bottom inner-right
      ];
      
      const usedPositions = svgSeatPositions.slice(0, Math.min(seats, svgSeatPositions.length));
      
      for (const svgPos of usedPositions) {
        const percentX = (svgPos.x - viewBoxX) / viewBoxWidth;
        const percentY = (svgPos.y - viewBoxY) / viewBoxHeight;
        
        positions.push({
          x: percentX * width - seatSize / 2,
          y: percentY * height - seatSize / 2,
        });
      }
      break;
    }
    default:
      // Default to circle pattern
      const r = (Math.min(width, height) / 2) + padding;
      for (let i = 0; i < seats; i++) {
        const angle = (2 * Math.PI * i) / seats - Math.PI / 2;
        positions.push({
          x: width / 2 + Math.cos(angle) * r - seatSize / 2,
          y: height / 2 + Math.sin(angle) * r - seatSize / 2,
        });
      }
  }
  
  return positions;
}

// Render custom shape SVG for non-standard shapes
function renderShapeSVG(shape, width, height, isSelected) {
  const strokeColor = isSelected ? '#D4AF37' : '#64748b';
  const fillColor = isSelected ? 'rgba(212, 175, 55, 0.3)' : 'rgba(51, 65, 85, 0.8)';
  
  switch (shape) {
    case 'lshape':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="absolute inset-0">
          <path 
            d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.75} L ${width} ${height * 0.75} L ${width} ${height} L 0 ${height} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'ushape':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="absolute inset-0">
          <path 
            d={`M 0 0 L ${width * 0.25} 0 L ${width * 0.25} ${height * 0.6} L ${width * 0.75} ${height * 0.6} L ${width * 0.75} 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'sshape':
      // Serpentine 2-arc table - Arc 1 UP, Arc 2 DOWN
      return (
        <svg width={width} height={height} viewBox="380 290 510 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 - curves UP */}
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 2 - curves DOWN */}
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'crescent':
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="absolute inset-0">
          <path 
            d={`M ${width * 0.1} ${height * 0.8} Q ${width * 0.1} ${height * 0.1} ${width * 0.5} ${height * 0.1} Q ${width * 0.9} ${height * 0.1} ${width * 0.9} ${height * 0.8} Q ${width * 0.7} ${height * 0.5} ${width * 0.5} ${height * 0.5} Q ${width * 0.3} ${height * 0.5} ${width * 0.1} ${height * 0.8} Z`}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    case 'serpentine3':
      // Serpentine 3-arc table - three arcs forming continuous wave
      // Arc 1 & 3 curve UP, Arc 2 (middle) curves DOWN (inverted)
      return (
        <svg width={width} height={height} viewBox="70 260 710 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 (Left) - curves UP - filled */}
          <path 
            d="M215.1,336.1c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 2 (Middle) - curves DOWN (inverted) - filled band */}
          <path 
            d={`
              M347.8,393.5
              c6.9,6.1,13.8,11.8,22,16.1c4.9,2.4,9.4,5.1,14.6,6.8c4.4,1.8,8.9,3.1,13.4,4.4c8.4,1.5,16.4,3.3,25,3.2c7.5.2,14.9-.3,22.3-1.9c12.5-1.8,24.7-7,35.7-13c5.6-3.3,10.8-7.1,15.9-11.1c1.7-1.5,3.2-3.2,5-4.6
              L566.6,458.1
              c-2.5,2.3-5.1,4.6-7.6,7c-3.4,3.4-7.7,6.2-11.4,9.3c-2.1,1.6-4.4,2.9-6.4,4.6c-4.1,3.4-8.8,6-13.3,8.8c-7,4.3-14.7,7.7-22.2,11.1c-8.3,4-16.9,6.4-25.6,9.2c-7.1,1.7-14.2,3.4-21.4,4.8c-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5c-6.8-.5-13.4-1.6-20-3c-5-.8-9.7-2.4-14.6-3.4c-2.4-.5-4.5-1.4-6.8-2.1c-8.8-2.2-16.8-5.8-25-9.4c-6.7-3.2-13.4-6.4-19.7-10.6c-13-7.6-25-17.6-36.1-27.8
              Z
            `}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 3 (Right) - curves UP - filled */}
          <path 
            d="M653,336c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'serpentine4':
      // Serpentine 4-arc table - four arcs forming continuous wave
      // Arc 1 & 3 curve UP, Arc 2 & 4 curve DOWN
      // ViewBox from user's SVG: 0 0 1700.8 850.4
      // We'll use a cropped version focusing on the table area
      return (
        <svg width={width} height={height} viewBox="380 290 950 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 (Left) - curves UP */}
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 2 - curves DOWN */}
          <path 
            d="M882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8"
            fill="none"
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6"
            fill="none"
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Fill for Arc 2 */}
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke="none"
          />
          
          {/* Arc 3 - curves UP */}
          <path 
            d="M968.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 4 (Right) - curves DOWN */}
          <path 
            d="M1101.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'serpentine5':
      // Serpentine 5-arc table - five arcs forming continuous wave
      // Arc 1, 3, 5 curve UP; Arc 2, 4 curve DOWN
      // Extended viewBox to accommodate 5 arcs
      return (
        <svg width={width} height={height} viewBox="380 290 1170 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 (Left) - curves UP */}
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 2 - curves DOWN - fill */}
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 3 - curves UP */}
          <path 
            d="M968.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 4 - curves DOWN - fill */}
          <path 
            d="M1101.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          
          {/* Arc 5 (Right) - curves UP - Arc 3 path shifted by 438 */}
          <path 
            d="M1406.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'serpentine6':
      // Serpentine 6-arc table - ends with arc DOWN
      return (
        <svg width={width} height={height} viewBox="380 290 1390 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 - curves UP */}
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 2 - curves DOWN - fill */}
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 3 - curves UP */}
          <path 
            d="M968.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 4 - curves DOWN - fill */}
          <path 
            d="M1101.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 5 - curves UP (Arc 3 + 438) */}
          <path 
            d="M1406.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 6 - curves DOWN (Arc 4 + 438) - fill */}
          <path 
            d="M1539.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'serpentine7':
      // Serpentine 7-arc table - ends with arc UP
      return (
        <svg width={width} height={height} viewBox="380 290 1610 290" className="absolute inset-0" preserveAspectRatio="none">
          {/* Arc 1 - curves UP */}
          <path 
            d="M530.7,335.9c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6-.1-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 2 - curves DOWN */}
          <path 
            d="M663.4,393.3c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6 L882.2,457.9c-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5.1-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8 Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 3 - curves UP */}
          <path 
            d="M968.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 4 - curves DOWN */}
          <path 
            d="M1101.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 5 - curves UP (Arc 3 + 438) */}
          <path 
            d="M1406.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 6 - curves DOWN (Arc 4 + 438) */}
          <path 
            d="M1539.9,393.7c6.9,6.1,13.8,11.8,22,16.1,4.9,2.4,9.4,5.1,14.6,6.8,4.4,1.8,8.9,3.1,13.4,4.4,8.4,1.5,16.4,3.3,25,3.2,7.5.2,14.9-.3,22.3-1.9,12.5-1.8,24.7-7,35.7-13,5.6-3.3,10.8-7.1,15.9-11.1,1.7-1.5,3.2-3.2,5-4.6,1.5,1.2,2.7,2.6,4,4,15.7,15.5,31.2,31.2,46.9,46.5,4.5,4.9,9.5,9.3,14,14.2-2.5,2.3-5.1,4.6-7.6,7-3.4,3.4-7.7,6.2-11.4,9.3-2.1,1.6-4.4,2.9-6.4,4.6-4.1,3.4-8.8,6-13.3,8.8-7,4.3-14.7,7.7-22.2,11.1-8.3,4-16.9,6.4-25.6,9.2-7.1,1.7-14.2,3.4-21.4,4.8-8.2,1.3-16.6,2-24.9,2.4h0c-9.5,0-19.1.5-28.5-.5-6.8-.5-13.4-1.6-20-3-5-.8-9.7-2.4-14.6-3.4-2.4-.5-4.5-1.4-6.8-2.1-8.8-2.2-16.8-5.8-25-9.4-6.7-3.2-13.4-6.4-19.7-10.6-13-7.6-25-17.6-36.1-27.8.4-.9.8-1.5,1.5-2.2"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Arc 7 - curves UP (Arc 5 + 438) */}
          <path 
            d="M1844.6,335.8c8.3.4,16.7,1.1,24.9,2.4,7.2,1.4,14.3,3.1,21.4,4.8,8.7,2.8,17.3,5.2,25.6,9.2,7.5,3.4,15.2,6.8,22.2,11.1,4.5,2.8,9.2,5.4,13.3,8.8,2,1.7,4.3,3,6.4,4.6,3.7,3.1,8,5.9,11.4,9.3,2.5,2.4,5.1,4.7,7.6,7-4.5,4.9-9.5,9.3-14,14.2-15.7,15.3-31.2,31-46.9,46.5-1.3,1.4-2.5,2.8-4,4-1.8-1.4-3.3-3.1-5-4.6-5.1-4-10.3-7.8-15.9-11.1-11-6-23.2-11.2-35.7-13-7.4-1.6-14.8-2.1-22.3-1.9-8.6,0-16.6,1.7-25,3.2-4.5,1.3-9,2.6-13.4,4.4-5.2,1.7-9.7,4.4-14.6,6.8-8.2,4.3-15.1,10-22,16.1-12.9-12.6-25.3-25.5-38.2-38-8.4-8.3-16.6-16.7-25-24.8-.7-.7-1.1-1.3-1.5-2.2,11.1-10.2,23.1-20.2,36.1-27.8,6.3-4.2,13-7.4,19.7-10.6,8.2-3.6,16.2-7.2,25-9.4,2.3-.7,4.4-1.6,6.8-2.1,4.9-1,9.6-2.6,14.6-3.4,6.6-1.4,13.2-2.5,20-3,9.4-1,19-.6,28.5-.6h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    case 'halfround':
      // Half Round table - 2 arcs forming horseshoe/U-shape
      // ViewBox matches exact SVG content bounds: 200-650 x 310-540 = 450 x 230
      return (
        <svg width={width} height={height} viewBox="200 310 450 230" className="absolute inset-0" preserveAspectRatio="xMidYMid meet">
          {/* Left arc - curves from top-center down to bottom-left */}
          <path 
            d="M278.2,401.9c6.8-6.7,13.3-13.7,20.7-19.6,5.2-4.4,10.7-8.3,16.4-11.9,4.1-2.9,8.6-5.1,12.8-7.8,2.1-1.3,4.2-2.2,6.3-3.3,7.8-4.6,16-7.7,24.4-10.8,7-2.4,14-4.8,21.5-6.3,14.6-3.7,30.2-5,45.2-5.5.3.9.5,1.6.5,2.6-.3,11.7-.3,23.4-.4,35.2-.4,18-.2,35.8-.6,53.9-9.2.5-18.1,1.3-27,4-5.2,1.7-10.3,3-15.2,5.4-4.4,1.8-8.5,4-12.6,6.3-7,4.8-14,9.2-20.1,15.3-5.5,5.1-10.4,10.7-14.6,17-7.6,10.1-12.7,22.3-16.3,34.3-1.7,6.3-2.7,12.6-3.5,19.1-.2,2.3,0,4.5-.3,6.8-1.9.2-3.7,0-5.7,0-22.1,0-44.1-.3-66-.2-6.6-.3-13.3,0-19.9-.3.2-3.4.4-6.9.5-10.3,0-4.8,1.1-9.8,1.6-14.6.4-2.6,1.1-5.2,1.3-7.8.5-5.3,2.1-10.4,3.3-15.6,2-8,5.1-15.8,8-23.5,3.1-8.7,7.6-16.4,11.8-24.5,3.9-6.2,7.7-12.4,11.9-18.4,4.9-6.7,10.4-13.1,16.1-19.2h0Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
          {/* Right arc - curves from top-center down to bottom-right */}
          <path 
            d="M426.6,336.8c15.1.7,30.6,2.2,45.1,6.1,7.4,1.5,14.4,4,21.4,6.6,8.3,3.3,16.5,6.5,24.3,11.2,2.1,1.1,4.2,2,6.3,3.4,4.2,2.8,8.6,5,12.7,8,5.6,3.7,11.1,7.6,16.2,12.1,7.3,6,13.8,13.1,20.5,19.9h0c5.5,6.3,11,12.7,15.8,19.5,4.1,6.1,7.8,12.4,11.6,18.6,4.1,8.2,8.5,16,11.4,24.7,2.9,7.7,5.8,15.6,7.7,23.6,1.2,5.2,2.6,10.3,3.1,15.6.2,2.6.9,5.2,1.2,7.8.4,4.8,1.4,9.8,1.4,14.6,0,3.5.2,6.9.4,10.3-6.6.2-13.3-.2-19.9,0-21.9-.4-44-.4-66-.7-1.9,0-3.7.2-5.7,0-.3-2.3-.1-4.5-.2-6.8-.7-6.4-1.7-12.8-3.3-19.1-3.5-12-8.3-24.4-15.8-34.5-4.1-6.4-8.9-12-14.3-17.2-6-6.2-12.9-10.6-19.8-15.5-4.1-2.3-8.2-4.6-12.5-6.4-4.9-2.5-9.9-3.8-15.1-5.6-8.8-2.8-17.7-3.7-26.9-4.3"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2.8"
            strokeMiterlimit="10"
          />
        </svg>
      );
    default:
      return null;
  }
}

export default function TableShape({ 
  table, 
  zoom = 1, 
  isSelected = false,
  onClick,
  onPositionChange,
  onUpdateLabel, // callback to update label offset
  onResize, // NEW: callback for resizing table
  onRotate, // NEW: callback for rotating table
  onDuplicate,
  onDelete,
  gridSize = 20,
  isLibraryItem = false,
  guests = [], // guests assigned to this table for hover display
  showSeats = true, // whether to show seat indicators
  onEmptySeatClick, // callback for clicking empty seat (Quick Assign)
}) {
  const shapeRef = useRef(null);
  const [hoveredSeatIndex, setHoveredSeatIndex] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotateStart, setRotateStart] = useState(null);
  
  // Manual drag state for rotated tables (bypasses react-dnd issues with CSS transforms)
  const [isManualDragging, setIsManualDragging] = useState(false);
  const [manualDragStart, setManualDragStart] = useState(null);
  const [manualDragOffset, setManualDragOffset] = useState({ x: 0, y: 0 });
  
  const shape = table.shape || 'round';
  const config = SHAPE_CONFIGS[shape] || SHAPE_CONFIGS.round;
  const rotation = table.rotation || 0;
  const isRotated = rotation !== 0;
  
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'TABLE',
    item: () => ({
      id: table.id,
      type: 'TABLE',
      shape: shape,
      table_name: table.table_name,
      seats: table.seats || table.capacity || 8,
      width: table.width,
      height: table.height,
      rotation: rotation,
    }),
    // Disable react-dnd drag for rotated tables - we handle them manually
    canDrag: !isResizing && !isRotating && !isRotated && !isManualDragging,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Hide default browser drag preview - CustomDragLayer handles it
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Apply drag ref
  drag(shapeRef);

  // Calculate display properties - use custom size if provided, otherwise defaults
  const originalWidth = table.width || config.width;
  const originalHeight = table.height || config.height;
  let baseWidth = originalWidth;
  let baseHeight = originalHeight;
  
  // For round tables, ensure perfect circle by using max dimension for both
  if (shape === 'round' || shape === 'circle') {
    const maxDim = Math.max(baseWidth, baseHeight);
    baseWidth = maxDim;
    baseHeight = maxDim;
  }
  
  const displayWidth = baseWidth * zoom;
  const displayHeight = baseHeight * zoom;
  
  // Get seat positions for this table - use original dimensions for positioning
  // but calculate seats as a perfect circle centered on the table
  const seats = table.seats || table.capacity || 8;
  const seatPositions = getSeatPositions(config.seatPattern || shape, seats, baseWidth, baseHeight);
  
  // Check if shape needs custom SVG rendering
  const needsCustomSVG = ['lshape', 'ushape', 'sshape', 'crescent', 'serpentine3', 'serpentine4', 'serpentine5', 'serpentine6', 'serpentine7', 'halfround'].includes(shape);

  // Snap to grid helper
  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);

  // ============ MANUAL DRAG HANDLING FOR ROTATED TABLES ============
  // This bypasses react-dnd which gets confused by CSS transforms
  
  const handleManualDragStart = useCallback((e) => {
    if (!isRotated || isResizing || isRotating) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsManualDragging(true);
    setManualDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      tableX: table.position_x || 0,
      tableY: table.position_y || 0,
    });
    setManualDragOffset({ x: 0, y: 0 });
  }, [isRotated, isResizing, isRotating, table.position_x, table.position_y]);

  const handleManualDragMove = useCallback((e) => {
    if (!isManualDragging || !manualDragStart) return;
    
    // Calculate how much the mouse moved (in canvas units, accounting for zoom)
    const deltaX = (e.clientX - manualDragStart.mouseX) / zoom;
    const deltaY = (e.clientY - manualDragStart.mouseY) / zoom;
    
    setManualDragOffset({ x: deltaX, y: deltaY });
  }, [isManualDragging, manualDragStart, zoom]);

  const handleManualDragEnd = useCallback((e) => {
    if (!isManualDragging || !manualDragStart) return;
    
    // Calculate final position
    const deltaX = (e.clientX - manualDragStart.mouseX) / zoom;
    const deltaY = (e.clientY - manualDragStart.mouseY) / zoom;
    
    const newX = snapToGrid(manualDragStart.tableX + deltaX);
    const newY = snapToGrid(manualDragStart.tableY + deltaY);
    
    // Update position through the callback
    // onPositionChange expects (x, y) not (id, data)
    if (onPositionChange) {
      onPositionChange(newX, newY);
    }
    
    setIsManualDragging(false);
    setManualDragStart(null);
    setManualDragOffset({ x: 0, y: 0 });
  }, [isManualDragging, manualDragStart, zoom, snapToGrid, onPositionChange]);

  // Attach global mouse event listeners for manual drag
  useEffect(() => {
    if (isManualDragging) {
      const handleMouseMove = (e) => handleManualDragMove(e);
      const handleMouseUp = (e) => handleManualDragEnd(e);
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isManualDragging, handleManualDragMove, handleManualDragEnd]);
  // ============ END MANUAL DRAG HANDLING ============

  // Handle resize start
  const handleResizeStart = useCallback((e, position) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      position,
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: baseWidth,
      height: baseHeight,
      x: table.position_x || 0,
      y: table.position_y || 0,
    });
  }, [baseWidth, baseHeight, table.position_x, table.position_y]);

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStart || !onResize) return;

    const deltaX = (e.clientX - resizeStart.mouseX) / zoom;
    const deltaY = (e.clientY - resizeStart.mouseY) / zoom;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = resizeStart.x;
    let newY = resizeStart.y;

    const { position } = resizeStart;
    const minWidth = config.minWidth || 50;
    const minHeight = config.minHeight || 50;

    // Calculate new dimensions based on resize handle position
    if (position.includes('e')) {
      newWidth = Math.max(minWidth, resizeStart.width + deltaX);
    }
    if (position.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStart.width - minWidth);
      newWidth = resizeStart.width - widthDelta;
      newX = resizeStart.x + widthDelta;
    }
    if (position.includes('s')) {
      newHeight = Math.max(minHeight, resizeStart.height + deltaY);
    }
    if (position.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStart.height - minHeight);
      newHeight = resizeStart.height - heightDelta;
      newY = resizeStart.y + heightDelta;
    }

    // For round/oval shapes, keep aspect ratio
    if (shape === 'round' || shape === 'circle') {
      const maxDim = Math.max(newWidth, newHeight);
      newWidth = maxDim;
      newHeight = maxDim;
    }

    // For L-shape: allow horizontal (width) resize, keep height fixed
    // L-shape extends horizontally, so resize from the right side
    if (shape === 'lshape') {
      // Keep original height (vertical arm length stays the same)
      newHeight = resizeStart.height;
      // Reset Y position since height doesn't change
      newY = resizeStart.y;
    }

    // For U-shape: allow vertical (height) resize, keep width fixed
    // U-shape has fixed horizontal width between the two arms
    if (shape === 'ushape') {
      // Keep original width (distance between arms stays the same)
      newWidth = resizeStart.width;
      // Reset X position since width doesn't change
      newX = resizeStart.x;
    }

    // Snap to grid
    newWidth = snapToGrid(newWidth);
    newHeight = snapToGrid(newHeight);
    newX = snapToGrid(newX);
    newY = snapToGrid(newY);

    // Calculate new seats based on size
    const newSeats = calculateAutoSeats(shape, newWidth, newHeight);

    // Update - explicitly include shape to prevent it from being reset
    onResize(table.id, {
      width: newWidth,
      height: newHeight,
      position_x: newX,
      position_y: newY,
      seats: newSeats,
      capacity: newSeats,
      shape: shape, // Preserve the shape during resize
    });
  }, [isResizing, resizeStart, zoom, config, shape, snapToGrid, onResize, table.id]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeStart(null);
  }, []);

  // Handle rotation start
  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get the center of the table
    const rect = shapeRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    setIsRotating(true);
    setRotateStart({
      centerX,
      centerY,
      startAngle: Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI),
      currentRotation: table.rotation || 0,
    });
  }, [table.rotation]);

  // Handle rotation move
  const handleRotateMove = useCallback((e) => {
    if (!isRotating || !rotateStart || !onRotate) return;
    
    const { centerX, centerY, startAngle, currentRotation } = rotateStart;
    
    // Calculate the current angle from center to mouse
    const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    
    // Calculate the delta angle
    let deltaAngle = currentAngle - startAngle;
    
    // Calculate new rotation
    let newRotation = currentRotation + deltaAngle;
    
    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360;
    
    // Snap to 15 degree increments when holding shift
    if (e.shiftKey) {
      newRotation = Math.round(newRotation / 15) * 15;
    }
    
    onRotate(table.id, { rotation: Math.round(newRotation) });
  }, [isRotating, rotateStart, onRotate, table.id]);

  // Handle rotation end
  const handleRotateEnd = useCallback(() => {
    setIsRotating(false);
    setRotateStart(null);
  }, []);

  // Global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Global mouse event listeners for rotation
  useEffect(() => {
    if (isRotating) {
      window.addEventListener('mousemove', handleRotateMove);
      window.addEventListener('mouseup', handleRotateEnd);
      return () => {
        window.removeEventListener('mousemove', handleRotateMove);
        window.removeEventListener('mouseup', handleRotateEnd);
      };
    }
  }, [isRotating, handleRotateMove, handleRotateEnd]);

  // Resize handle component
  const ResizeHandle = ({ position }) => {
    const cursorMap = {
      'nw': 'nw-resize', 'n': 'n-resize', 'ne': 'ne-resize',
      'w': 'w-resize', 'e': 'e-resize',
      'sw': 'sw-resize', 's': 's-resize', 'se': 'se-resize',
    };

    const positionStyles = {
      'nw': { top: -4, left: -4 },
      'n': { top: -4, left: '50%', transform: 'translateX(-50%)' },
      'ne': { top: -4, right: -4 },
      'w': { top: '50%', left: -4, transform: 'translateY(-50%)' },
      'e': { top: '50%', right: -4, transform: 'translateY(-50%)' },
      'sw': { bottom: -4, left: -4 },
      's': { bottom: -4, left: '50%', transform: 'translateX(-50%)' },
      'se': { bottom: -4, right: -4 },
    };

    return (
      <div
        className="absolute w-2 h-2 bg-white border border-[#D4AF37] z-30"
        style={{
          ...positionStyles[position],
          cursor: cursorMap[position],
        }}
        onMouseDown={(e) => handleResizeStart(e, position)}
      />
    );
  };

  // Rotation handle component - circular handle above the table
  const RotationHandle = () => {
    const handleDistance = 30; // Distance from top of table
    
    return (
      <>
        {/* Line connecting table to rotation handle */}
        <div
          className="absolute bg-[#D4AF37] z-20"
          style={{
            width: 1,
            height: handleDistance - 6,
            top: -handleDistance + 6,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
        {/* Rotation handle circle */}
        <div
          className="absolute w-4 h-4 rounded-full bg-[#D4AF37] border-2 border-white cursor-grab active:cursor-grabbing z-30 hover:scale-125 transition-transform flex items-center justify-center"
          style={{
            top: -handleDistance - 8,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
          onMouseDown={handleRotateStart}
          title="Drag to rotate (hold Shift for 15° snap)"
        >
          {/* Rotation icon */}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="w-2.5 h-2.5 text-white" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
        </div>
      </>
    );
  };
  
  // For library items, render a simpler preview
  if (isLibraryItem) {
    return (
      <div
        ref={shapeRef}
        className={`
          cursor-grab active:cursor-grabbing
          flex items-center justify-center
          relative
          ${!needsCustomSVG ? 'bg-slate-800 border-2 border-slate-600' : ''}
          hover:border-[#D4AF37] hover:bg-slate-700
          transition-all duration-200
          ${isDragging ? 'opacity-50' : 'opacity-100'}
        `}
        style={{
          width: config.width * 0.6,
          height: config.height * 0.6,
          borderRadius: needsCustomSVG ? '0' : config.borderRadius,
          overflow: 'visible',
        }}
        data-testid={`library-table-${shape}`}
      >
        {needsCustomSVG && renderShapeSVG(shape, config.width * 0.6, config.height * 0.6, false)}
        <span className="text-xs text-slate-400 font-medium relative z-10">
          {config.label}
        </span>
      </div>
    );
  }

  // Filter guests for this table and create a map by seat_number
  const tableGuests = guests.filter(g => g.table_id === table.id);
  const occupiedSeats = tableGuests.length;
  
  // Create a map of seat_number (1-based) to guest for proper seat assignment
  // seat_number is stored as 1-based in the database
  const guestBySeatNumber = {};
  tableGuests.forEach(guest => {
    if (guest.seat_number !== null && guest.seat_number !== undefined) {
      // Convert 1-based seat_number to 0-based index
      guestBySeatNumber[guest.seat_number - 1] = guest;
    }
  });
  
  // For guests without seat_number, assign them to first available seats
  const guestsWithoutSeat = tableGuests.filter(g => g.seat_number === null || g.seat_number === undefined);
  let nextAvailableSeatIdx = 0;
  guestsWithoutSeat.forEach(guest => {
    // Find next available seat
    while (guestBySeatNumber[nextAvailableSeatIdx] !== undefined) {
      nextAvailableSeatIdx++;
    }
    guestBySeatNumber[nextAvailableSeatIdx] = guest;
    nextAvailableSeatIdx++;
  });

  // Calculate actual display position (includes manual drag offset for rotated tables)
  const displayX = ((table.position_x || 0) + (isManualDragging ? manualDragOffset.x : 0)) * zoom;
  const displayY = ((table.position_y || 0) + (isManualDragging ? manualDragOffset.y : 0)) * zoom;

  // Render placed table on canvas
  return (
    <div
      ref={shapeRef}
      className={`
        absolute cursor-grab active:cursor-grabbing
        flex flex-col items-center justify-center
        ${isSelected ? 'ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-slate-900' : ''}
      `}
      style={{
        left: displayX,
        top: displayY,
        width: displayWidth,
        height: displayHeight,
        borderRadius: needsCustomSVG ? '0' : config.borderRadius,
        backgroundColor: needsCustomSVG ? 'transparent' : (isSelected ? 'rgba(212, 175, 55, 0.3)' : 'rgba(51, 65, 85, 0.8)'),
        border: needsCustomSVG ? 'none' : `2px solid ${isSelected ? '#D4AF37' : '#64748b'}`,
        transform: rotation ? `rotate(${rotation}deg)` : 'none',
        overflow: 'visible',
        opacity: isDragging ? 0 : (isManualDragging ? 0.7 : 1),
        transition: isManualDragging ? 'none' : undefined,
        zIndex: isManualDragging ? 1000 : undefined,
      }}
      onMouseDown={isRotated ? handleManualDragStart : undefined}
      onClick={(e) => {
        if (isManualDragging) return;
        e.stopPropagation();
        onClick?.(e);
      }}
      data-testid={`canvas-table-${table.id}`}
    >
      {/* Custom SVG for special shapes */}
      {needsCustomSVG && renderShapeSVG(shape, displayWidth, displayHeight, isSelected)}
      
      {/* Table Label (Name + Seats) - Click to enable moving */}
      <div
        className={`absolute flex flex-col items-center justify-center select-none transition-all ${isSelected ? 'cursor-move hover:bg-white/10 rounded px-2 py-1' : ''}`}
        style={{
          left: (table.label_offset_x || 0) * zoom + displayWidth / 2,
          top: (table.label_offset_y || 0) * zoom + displayHeight / 2,
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
        }}
        onMouseDown={(e) => {
          if (!isSelected || !onUpdateLabel) return;
          e.stopPropagation();
          e.preventDefault();
          
          const startX = e.clientX;
          const startY = e.clientY;
          const startOffsetX = table.label_offset_x || 0;
          const startOffsetY = table.label_offset_y || 0;
          
          const handleMouseMove = (moveEvent) => {
            const deltaX = (moveEvent.clientX - startX) / zoom;
            const deltaY = (moveEvent.clientY - startY) / zoom;
            
            // Clamp to stay within table bounds
            const maxOffsetX = displayWidth / (2 * zoom) - 20;
            const maxOffsetY = displayHeight / (2 * zoom) - 15;
            const newOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, startOffsetX + deltaX));
            const newOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, startOffsetY + deltaY));
            
            onUpdateLabel(table.id, { label_offset_x: newOffsetX, label_offset_y: newOffsetY });
          };
          
          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };
          
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
        data-testid={`table-label-${table.id}`}
      >
        {/* Table Name */}
        <span 
          className="text-xs font-semibold text-white text-center truncate px-1"
          style={{ maxWidth: displayWidth - 8 }}
        >
          {table.table_name}
        </span>
        
        {/* Seats Count */}
        <span className="text-[10px] text-slate-400 mt-0.5">
          {occupiedSeats}/{seats} seats
        </span>
        
        {/* Move hint when selected */}
        {isSelected && (
          <span className="text-[8px] text-[#D4AF37] mt-1 opacity-70">
            drag to move
          </span>
        )}
      </div>
      
      {/* Seat Indicators */}
      {showSeats && seatPositions.map((pos, idx) => {
        // Check if this specific seat index has a guest assigned
        const guestAtSeat = guestBySeatNumber[idx] || null;
        const isOccupied = guestAtSeat !== null;
        const isHovered = hoveredSeatIndex === idx;
        const canInteract = onEmptySeatClick; // Can interact with any seat if callback provided
        
        // Get individual seat offset if exists
        const seatOffsets = table.seat_offsets || {};
        const seatOffset = seatOffsets[idx] || { x: 0, y: 0 };
        const seatX = (pos.x + seatOffset.x) * zoom;
        const seatY = (pos.y + seatOffset.y) * zoom;
        
        // Larger clickable area for better UX (especially important for serpentine)
        const clickAreaSize = 22 * zoom;
        const seatDotSize = 10 * zoom;
        const clickAreaOffset = (clickAreaSize - seatDotSize) / 2;
        
        return (
          <div
            key={idx}
            className={`absolute ${isSelected ? 'cursor-move' : canInteract ? 'cursor-pointer' : ''}`}
            style={{
              // Position the wrapper so the visible seat dot appears at the correct location
              left: seatX - clickAreaOffset,
              top: seatY - clickAreaOffset,
              width: clickAreaSize,
              height: clickAreaSize,
              zIndex: isHovered ? 50 : 30,
            }}
            onMouseEnter={() => setHoveredSeatIndex(idx)}
            onMouseLeave={() => setHoveredSeatIndex(null)}
            onClick={(e) => {
              // Handle click on the larger area
              if (canInteract && !isSelected) {
                e.stopPropagation();
                const tableX = (table.position_x || 0) * zoom;
                const tableY = (table.position_y || 0) * zoom;
                onEmptySeatClick({
                  tableId: table.id,
                  tableName: table.table_name,
                  seatIndex: idx,
                  posX: tableX + seatX + seatDotSize / 2,
                  posY: tableY + seatY + seatDotSize,
                  currentGuest: guestAtSeat,
                  isOccupied: isOccupied,
                });
              }
            }}
            onMouseDown={(e) => {
              // Allow dragging seats when table is selected
              if (!isSelected || !onUpdateLabel) return;
              e.stopPropagation();
              e.preventDefault();
              
              const startX = e.clientX;
              const startY = e.clientY;
              const startOffsetX = seatOffset.x;
              const startOffsetY = seatOffset.y;
              
              const handleMouseMove = (moveEvent) => {
                const deltaX = (moveEvent.clientX - startX) / zoom;
                const deltaY = (moveEvent.clientY - startY) / zoom;
                
                const newOffsets = { ...seatOffsets };
                newOffsets[idx] = {
                  x: startOffsetX + deltaX,
                  y: startOffsetY + deltaY,
                };
                
                onUpdateLabel(table.id, { seat_offsets: newOffsets });
              };
              
              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            data-testid={`seat-clickarea-${idx}`}
          >
            {/* Visible seat dot - centered within the click area */}
            <div
              className={`
                absolute rounded-full border
                transition-all duration-200
                ${isOccupied 
                  ? 'bg-[#D4AF37] border-[#B5952F]' 
                  : 'bg-slate-700 border-slate-600'
                }
                ${isSelected 
                  ? 'ring-1 ring-white/30'
                  : ''
                }
                ${canInteract && !isSelected
                  ? 'cursor-pointer hover:scale-125' 
                  : ''
                }
                ${canInteract && !isOccupied && !isSelected
                  ? 'hover:bg-[#D4AF37]/50 hover:border-[#D4AF37]' 
                  : ''
                }
                ${canInteract && isOccupied && !isSelected
                  ? 'hover:bg-[#D4AF37] hover:border-[#FFD700]' 
                  : ''
                }
              `}
              style={{
                width: seatDotSize,
                height: seatDotSize,
                left: clickAreaOffset,
                top: clickAreaOffset,
              }}
              data-testid={`seat-indicator-${idx}${isOccupied ? '-occupied' : '-empty'}`}
            />
            
            {/* Individual guest tooltip */}
            {isHovered && guestAtSeat && (
              <div 
                className="absolute z-[100] pointer-events-none whitespace-nowrap"
                style={{
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: 4,
                }}
              >
                <div className="bg-slate-900 border border-slate-700 rounded px-2 py-1 shadow-lg">
                  <p className="text-xs text-white font-medium">
                    {guestAtSeat.full_name}
                  </p>
                </div>
                {/* Arrow */}
                <div 
                  className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
                  style={{
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '4px solid #1e293b',
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#D4AF37] rounded-full z-20" />
      )}

      {/* Resize Handles (only when selected and onResize is provided) */}
      {isSelected && onResize && (
        <>
          {/* L-shape: horizontal resize only (east/west handles) */}
          {shape === 'lshape' ? (
            <>
              <ResizeHandle position="e" />
              <ResizeHandle position="w" />
            </>
          ) : /* U-shape: vertical resize only (north/south handles) */
          shape === 'ushape' ? (
            <>
              <ResizeHandle position="n" />
              <ResizeHandle position="s" />
            </>
          ) : (
            /* All other shapes: full resize handles */
            <>
              <ResizeHandle position="nw" />
              <ResizeHandle position="n" />
              <ResizeHandle position="ne" />
              <ResizeHandle position="w" />
              <ResizeHandle position="e" />
              <ResizeHandle position="sw" />
              <ResizeHandle position="s" />
              <ResizeHandle position="se" />
            </>
          )}
        </>
      )}

      {/* Rotation Handle (only when selected and onRotate is provided) */}
      {isSelected && onRotate && !isLibraryItem && (
        <RotationHandle />
      )}

      {/* Floating Action Buttons (only when selected) */}
      {isSelected && !isLibraryItem && (
        <>
          {/* Duplicate button - top left (avoiding selection indicator) */}
          {onDuplicate && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                top: -10,
                left: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(table);
              }}
              title="Duplicate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          )}

          {/* Delete button - bottom right */}
          {onDelete && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                bottom: -10,
                right: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(table.id);
              }}
              title="Delete"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Export shape configs for use in library
export { SHAPE_CONFIGS, calculateAutoSeats };
